import { ShieldCheck, Lock, Buildings, Sparkle, CloudArrowUp, FilePdf, FileZip } from "@phosphor-icons/react";
import { toast } from "sonner";
import api from "../lib/api";

// Customer-facing data-security statement shown in Admin → Security.
// Static content — safe to edit the copy below to match your own policies.
const POINTS = [
  {
    icon: Lock,
    title: "Encrypted at rest (two layers)",
    body: "Two independent layers protect your data at rest. The production database is encrypted with AES-256 at the storage layer, and uploaded files plus the most sensitive fields (MFA secrets, confidential BCSI) are additionally encrypted at the application level using Fernet authenticated encryption (AES-128-CBC with HMAC). A raw copy of the storage is unreadable ciphertext without the keys.",
  },
  {
    icon: Buildings,
    title: "Isolated to your entity",
    body: "Your files and records are scoped to your registered entity. Users from one organization cannot see, search, or access another organization's data — tenant isolation is enforced on every request.",
  },
  {
    icon: ShieldCheck,
    title: "Access-controlled",
    body: "Only authenticated, authorized members of your entity can open a document — protected by secure sign-in, salted bcrypt password hashes, multi-factor authentication, brute-force lockout, and role-based permissions.",
  },
  {
    icon: ShieldCheck,
    title: "You control your own security posture",
    body: "Each entity administers its own controls from Admin → Security: require MFA for every member (with a grace period), set an inactivity session timeout, and manage roles — independently of any other organization on the platform.",
  },
  {
    icon: ShieldCheck,
    title: "SOC 2 readiness",
    body: "ClaroShield operates a documented SOC 2 readiness program (information security, access control, change management, incident response, system architecture) — designed for a Type I examination first, then a Type II observation window.",
  },
  {
    icon: Sparkle,
    title: "AI that doesn't keep or train on your data",
    body: "When you ask the AI to review a document, the content is sent over an encrypted connection to the AI provider's business API (OpenAI / Anthropic) — not the consumer ChatGPT app. Your content is NOT used to train any AI model, is retained only briefly for abuse monitoring (typically up to 30 days), and is then deleted. It is never reused for any other purpose.",
  },
  {
    icon: CloudArrowUp,
    title: "You control the AI",
    body: "AI assistance is optional and entirely in your hands. Use the AI & Data Controls below to choose Full AI, Public-information only (AI runs on public-standard tools but is blocked from your confidential asset and evidence data), or AI off — at any time, based on your organization's appetite for AI usage.",
  },
];

export default function DataSecurityNotice() {
  const openPdf = async (path, label) => {
    try {
      const res = await api.get(path, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      window.open(url, "_blank", "noopener");
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch {
      toast.error(`Couldn't open the ${label}. Please try again.`);
    }
  };

  const DOCS = [
    { path: "/security-overview", label: "Security Overview", testid: "open-security-overview-btn", primary: true },
    { path: "/subprocessor-list", label: "Subprocessor List", testid: "open-subprocessor-list-btn" },
    { path: "/provider-data-terms", label: "Provider Data Terms", testid: "open-provider-terms-btn" },
    { path: "/dpa-template", label: "DPA Template", testid: "open-dpa-template-btn" },
  ];

  const downloadPack = async () => {
    try {
      const res = await api.get("/auditor-pack", { responseType: "blob" });
      const cd = res.headers?.["content-disposition"] || "";
      const m = cd.match(/filename="?([^"]+)"?/);
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = m ? m[1] : "Auditor_Pack.zip";
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 60000);
      toast.success("Auditor pack downloaded.");
    } catch {
      toast.error("Couldn't build the auditor pack. Please try again.");
    }
  };

  return (
    <div className="border border-border bg-card p-6" data-testid="data-security-notice">
      <div className="flex items-start gap-3">
        <ShieldCheck size={24} weight="fill" className="text-primary mt-0.5 shrink-0" />
        <div>
          <h3 className="font-display font-bold text-lg">Your Data &amp; Security</h3>
          <p className="text-sm text-muted-foreground mt-1 max-w-3xl">
            The documents you upload to do your compliance work — policies, procedures, evidence,
            RSAWs, transcripts and more — are treated as sensitive by design. Here's how they're
            protected.
          </p>
        </div>
      </div>

      <div className="mt-5 grid gap-4 sm:grid-cols-2">
        {POINTS.map((p) => (
          <div key={p.title} className="flex gap-3 border border-border p-4" data-testid={`security-point-${p.title.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "")}`}>
            <p.icon size={20} className="text-primary mt-0.5 shrink-0" />
            <div>
              <div className="font-semibold text-sm">{p.title}</div>
              <p className="text-sm text-muted-foreground mt-1">{p.body}</p>
            </div>
          </div>
        ))}
      </div>

      <div className="mt-4 border border-border bg-muted px-4 py-3">
        <div className="text-sm text-muted-foreground mb-3">
          Need this in writing for your own due-diligence file? Open any of these one-page documents to
          save, print, or share with your customers.
        </div>
        <div className="flex flex-wrap gap-2">
          {DOCS.map((d) => (
            <button
              key={d.path}
              type="button"
              data-testid={d.testid}
              onClick={() => openPdf(d.path, d.label)}
              className={`inline-flex items-center gap-2 px-4 py-2 text-sm font-semibold transition-colors border ${
                d.primary
                  ? "border-primary bg-primary text-primary-foreground hover:bg-primary/90"
                  : "border-border bg-card text-foreground hover:border-primary"
              }`}>
              <FilePdf size={18} weight={d.primary ? "fill" : "regular"} /> {d.label}
            </button>
          ))}
          <button
            type="button"
            data-testid="download-auditor-pack-btn"
            onClick={downloadPack}
            className="inline-flex items-center gap-2 px-4 py-2 text-sm font-semibold transition-colors border border-primary/50 bg-primary/5 text-primary hover:bg-primary/10">
            <FileZip size={18} weight="fill" /> Download all as auditor pack (.zip)
          </button>
        </div>
        <p className="text-xs text-muted-foreground mt-3">
          The DPA is a starting template to review with your legal counsel — it is not legal advice. For an
          upstream (platform) DPA or any security certifications, contact Emergent support.
        </p>
      </div>
    </div>
  );
}
